import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';
import { requiredClientText } from './clients-legal-input.js';

// Field bounds — exported so web forms can render input limits without
// duplicating literals. `details` keeps the legacy 2000-char bound of the flat
// `ClientLegalEntity.requisites` column it replaces (#2242 FR-2).
export const LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH = 2000;
export const LEGAL_ENTITY_REQUISITES_BANK_NAME_MAX_LENGTH = 200;
export const LEGAL_ENTITY_REQUISITES_BIK_MAX_LENGTH = 20;
export const LEGAL_ENTITY_REQUISITES_ACCOUNT_MAX_LENGTH = 34;

/**
 * @aiPurpose wire shape for one `LegalEntityRequisites` row nested under a
 *   `ClientLegalEntity` (#2242 FR-2). Replaces the flat
 *   `ClientLegalEntity.requisites String` column — each legal entity carries
 *   1..n of these. The canonical (first active) row's `details` is mirrored
 *   back on the legacy `ClientLegalEntity.requisites` wire field so existing
 *   readers keep working while storage moves.
 * @aiPii `details` is free-form business text; `bik` and `account` are
 *   sensitive bank identifiers (PARTIAL-blocked in the Agent catalog). Redact
 *   in logs.
 */
export const LegalEntityRequisitesSchema = z
  .object({
    id: z.string().uuid(),
    legalEntityId: z.string().uuid(),
    details: z.string().min(1).max(LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH),
    bankName: z.string().min(1).max(LEGAL_ENTITY_REQUISITES_BANK_NAME_MAX_LENGTH).nullable(),
    bik: z.string().min(1).max(LEGAL_ENTITY_REQUISITES_BIK_MAX_LENGTH).nullable(),
    account: z.string().min(1).max(LEGAL_ENTITY_REQUISITES_ACCOUNT_MAX_LENGTH).nullable(),
    archivedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type LegalEntityRequisites = z.infer<typeof LegalEntityRequisitesSchema>;

/**
 * @aiPurpose input for `POST /api/clients/:id/legal-entities/:leId/requisites`.
 *   `details` is required (min 1) — it carries the requisites content and
 *   satisfies the "≥1 requisites set per entity" invariant; the structured bank
 *   fields are optional going-forward additions. `bankName` / `bik` / `account`
 *   may be omitted or explicitly `null` (a form that always serialises the field
 *   clears the slot symmetrically), mirroring the `ClientContact.comment`
 *   convention.
 */
export const LegalEntityRequisitesInputSchema = z
  .object({
    details: requiredClientText(LEGAL_ENTITY_REQUISITES_DETAILS_MAX_LENGTH),
    bankName: z
      .string()
      .min(1)
      .max(LEGAL_ENTITY_REQUISITES_BANK_NAME_MAX_LENGTH)
      .nullable()
      .optional(),
    bik: z.string().min(1).max(LEGAL_ENTITY_REQUISITES_BIK_MAX_LENGTH).nullable().optional(),
    account: z
      .string()
      .min(1)
      .max(LEGAL_ENTITY_REQUISITES_ACCOUNT_MAX_LENGTH)
      .nullable()
      .optional(),
  })
  .strict();
export type LegalEntityRequisitesInput = z.infer<typeof LegalEntityRequisitesInputSchema>;

export const PatchLegalEntityRequisitesInputSchema = nonEmptyPatch(
  LegalEntityRequisitesInputSchema.partial().strict(),
);
export type PatchLegalEntityRequisitesInput = z.infer<typeof PatchLegalEntityRequisitesInputSchema>;
